import { useEffect, useId, useState } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Input,
  Label,
  SelectDropdown,
  Textarea,
  type SelectDropdownOption,
} from "@coval/ui"
import { Eye, EyeOff } from "lucide-react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import {
  DEEPGRAM_LISTEN_MODEL_SUGGESTIONS,
  DEEPGRAM_SPEAK_MODEL_SUGGESTIONS,
  DEEPGRAM_THINK_PROVIDERS,
  getDeepgramGuidedSettings,
  updateDeepgramGuidedSetting,
  type DeepgramGuidedSetting,
} from "@/utils/simulation/deepgramRealtimeSettings"
import {
  getJsonArrayError,
  getJsonObjectError,
  getTimeoutSecondsError,
  parseTimeoutSecondsInput,
} from "@/utils/simulation/realtimeFieldValidation"

interface DeepgramRealtimeFieldsProps {
  apiKey: string
  prompt?: string
  functions?: string
  settings?: string
  timeoutSeconds?: number
  requiredFieldKeys?: ReadonlySet<string>
  disabled?: boolean
  className?: string
  onApiKeyChange: (value: string) => void
  onPromptChange: (value: string) => void
  onFunctionsChange: (value: string) => void
  onSettingsChange: (value: string) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
}

const CUSTOM_MODEL_VALUE = "__custom_model__"

interface GuidedModelSelectProps {
  label: string
  value: string
  suggestions: readonly string[]
  helpText: string
  disabled: boolean
  className: string
  onChange: (value: string) => void
}

const GuidedModelSelect: React.FC<GuidedModelSelectProps> = ({
  label,
  value,
  suggestions,
  helpText,
  disabled,
  className,
  onChange,
}) => {
  const labelId = useId()
  const customModelId = useId()
  const usesSuggestedModel = suggestions.includes(value)
  const [usesCustomModel, setUsesCustomModel] = useState(!usesSuggestedModel)

  useEffect(() => {
    setUsesCustomModel(!usesSuggestedModel)
  }, [usesSuggestedModel, value])

  const options: SelectDropdownOption[] = [
    ...suggestions.map((model) => ({ value: model, label: model })),
    {
      value: CUSTOM_MODEL_VALUE,
      label: "Custom model ID…",
      description: "Enter a current model ID that is not listed above.",
    },
  ]

  return (
    <div className="space-y-2">
      <Label id={labelId}>{label}</Label>
      <SelectDropdown
        aria-labelledby={labelId}
        options={options}
        value={
          usesCustomModel || !usesSuggestedModel ? CUSTOM_MODEL_VALUE : value
        }
        onValueChange={(selectedValue) => {
          if (selectedValue === CUSTOM_MODEL_VALUE) {
            setUsesCustomModel(true)
            return
          }
          setUsesCustomModel(false)
          onChange(selectedValue)
        }}
        searchable
        searchPlaceholder={`Search ${label.toLowerCase()}s`}
        disabled={disabled}
        className={className}
      />
      {(usesCustomModel || !usesSuggestedModel) && (
        <div className="space-y-2">
          <Label htmlFor={customModelId}>Custom model ID</Label>
          <Input
            id={customModelId}
            value={usesSuggestedModel ? "" : value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Enter a model ID"
            disabled={disabled}
            className={className}
          />
        </div>
      )}
      <p className="text-xs text-coval-text-muted">{helpText}</p>
    </div>
  )
}

/** Fields for the hosted Deepgram Voice Agent Settings payload. */
export const DeepgramRealtimeFields: React.FC<DeepgramRealtimeFieldsProps> = ({
  apiKey,
  prompt,
  functions,
  settings,
  timeoutSeconds,
  requiredFieldKeys = new Set(),
  onApiKeyChange,
  onPromptChange,
  onFunctionsChange,
  onSettingsChange,
  onTimeoutSecondsChange,
  disabled,
  className = "",
}) => {
  const apiKeyId = useId()
  const thinkProviderLabelId = useId()
  const greetingId = useId()
  const functionsHelpId = useId()
  const settingsHelpId = useId()
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)
  const functionsError = getJsonArrayError(functions)
  const settingsError = getJsonObjectError(settings)
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)
  const guidedSettings = getDeepgramGuidedSettings(settings)
  const selectedThinkProvider = DEEPGRAM_THINK_PROVIDERS.find(
    (provider) => provider.value === guidedSettings.thinkProvider,
  )
  const guidedFieldsDisabled = disabled || !guidedSettings.editable
  const isRequired = (key: string) => requiredFieldKeys.has(key)
  const optionalSuffix = (key: string) => (isRequired(key) ? "" : " (Optional)")
  const updateGuidedSetting = (field: DeepgramGuidedSetting, value: string) =>
    onSettingsChange(updateDeepgramGuidedSetting(settings, field, value))

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={apiKeyId}>Deepgram API Key</Label>
        <Input
          id={apiKeyId}
          type={isApiKeyVisible ? "text" : "password"}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore
          placeholder="Your Deepgram API key"
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={className}
          disabled={disabled}
          required
          endAdornment={
            <button
              type="button"
              onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
              aria-label={isApiKeyVisible ? "Hide API key" : "Show API key"}
            >
              {isApiKeyVisible ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          }
        />
      </div>
      <div className="space-y-2">
        <Label>
          Agent System Prompt{optionalSuffix("deepgram_realtime_prompt")}
        </Label>
        <Textarea
          placeholder="You are a friendly customer service agent for Acme Corp. Be concise and helpful. Always confirm understanding before taking actions."
          value={prompt || ""}
          onChange={(e) => onPromptChange(e.target.value)}
          className={`text-sm ${className}`}
          disabled={disabled}
          rows={4}
          aria-label="Agent System Prompt"
          required={isRequired("deepgram_realtime_prompt")}
        />
      </div>

      <div className="space-y-4 rounded-lg border border-coval-border bg-surface-1 p-4">
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-coval-text">
            Voice pipeline
          </h3>
          <p className="text-xs text-coval-text-muted">
            Configure the common listen, think, and speak settings. Model fields
            accept any current Deepgram model ID, so newer models are not
            blocked by a fixed dropdown.
          </p>
        </div>
        {!guidedSettings.editable && (
          <p className="rounded border border-coval-warning-border bg-coval-warning-surface px-3 py-2 text-xs text-coval-warning-text">
            This agent uses invalid JSON, provider fallbacks, or a custom
            endpoint or voice provider. Keep editing it in Advanced Settings;
            guided fields are paused to avoid overwriting unsupported values.
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          <GuidedModelSelect
            label="Speech recognition model"
            value={guidedSettings.listenModel}
            suggestions={DEEPGRAM_LISTEN_MODEL_SUGGESTIONS}
            helpText="Nova uses v1; Flux uses v2. Coval keeps the required version selector in sync."
            disabled={guidedFieldsDisabled}
            className={className}
            onChange={(value) => updateGuidedSetting("listenModel", value)}
          />
          <GuidedModelSelect
            label="Agent voice model"
            value={guidedSettings.speakModel}
            suggestions={DEEPGRAM_SPEAK_MODEL_SUGGESTIONS}
            helpText="Flux voices use v2; Aura voices use v1. Coval pairs the selected model with the right version."
            disabled={guidedFieldsDisabled}
            className={className}
            onChange={(value) => updateGuidedSetting("speakModel", value)}
          />
          <div className="space-y-2">
            <Label id={thinkProviderLabelId}>Language model provider</Label>
            <SelectDropdown
              aria-labelledby={thinkProviderLabelId}
              options={DEEPGRAM_THINK_PROVIDERS.map((provider) => ({
                value: provider.value,
                label: provider.label,
              }))}
              value={guidedSettings.thinkProvider}
              onValueChange={(value) => {
                if (value) {
                  updateGuidedSetting("thinkProvider", value)
                }
              }}
              disabled={guidedFieldsDisabled}
              className={className}
            />
            <p className="text-xs text-coval-text-muted">
              Selecting a managed provider also switches to a compatible model.
              Groq, Bedrock, and custom endpoints remain in Advanced Settings.
            </p>
          </div>
          <GuidedModelSelect
            label="Language model"
            value={guidedSettings.thinkModel}
            suggestions={selectedThinkProvider?.models ?? []}
            helpText="Choose a current managed model or use a custom model ID. Full provider configuration remains editable below."
            disabled={guidedFieldsDisabled}
            className={className}
            onChange={(value) => updateGuidedSetting("thinkModel", value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={greetingId}>Agent greeting (Optional)</Label>
          <Input
            id={greetingId}
            value={guidedSettings.greeting}
            onChange={(event) =>
              updateGuidedSetting("greeting", event.target.value)
            }
            placeholder="Thanks for calling! How can I help?"
            disabled={guidedFieldsDisabled}
            className={className}
          />
          <p className="text-xs text-coval-text-muted">
            Required when the agent should speak before the persona.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Timeout{optionalSuffix("simulation_timeout_seconds")}</Label>
          <Input
            type="number"
            placeholder="900"
            min={1}
            max={1800}
            value={timeoutSeconds?.toString() || ""}
            onChange={(e) =>
              onTimeoutSecondsChange(parseTimeoutSecondsInput(e.target.value))
            }
            className={className}
            disabled={disabled}
            error={!!timeoutError}
            required={isRequired("simulation_timeout_seconds")}
          />
          {timeoutError && (
            <p className="text-xs text-coval-danger-text">{timeoutError}</p>
          )}
        </div>
      </div>

      <Accordion type="single" collapsible className="px-0">
        <AccordionItem value="deepgram-realtime-advanced">
          <AccordionTrigger>
            <div className="flex flex-col items-start gap-0.5 text-left">
              <span>Advanced Settings</span>
              <span className="text-xs text-coval-text-muted font-normal">
                Tool calls, endpoints, audio, fallbacks, and full Settings JSON
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-5 pt-2">
              <div className="space-y-2">
                <Label>
                  Agent Tool Definitions
                  {optionalSuffix("deepgram_realtime_functions")}
                </Label>
                <JsonCodeEditor
                  placeholder='[{"name": "get_weather", "description": "Get the weather for a location", "parameters": {"type": "object", "properties": {"location": {"type": "string"}}, "required": ["location"]}}]'
                  value={functions || ""}
                  onChange={onFunctionsChange}
                  className={className}
                  disabled={disabled}
                  rows={5}
                  ariaLabel="Agent Tool Definitions"
                  ariaDescribedBy={functionsHelpId}
                  ariaRequired={isRequired("deepgram_realtime_functions")}
                  error={functionsError}
                />
                <p
                  id={functionsHelpId}
                  className="text-xs text-coval-text-muted"
                >
                  Each item needs a name and may include description,
                  parameters, and endpoint. With an endpoint, Deepgram runs the
                  tool. Without one, Coval records the call and returns a
                  simulated success response; it does not execute customer code.
                </p>
              </div>
              <div className="space-y-2">
                <Label>
                  Full Deepgram Settings JSON
                  {optionalSuffix("deepgram_realtime_settings")}
                </Label>
                <JsonCodeEditor
                  placeholder='{"agent": {"listen": {"provider": {"type": "deepgram", "model": "nova-3"}}, "speak": {"provider": {"type": "deepgram", "version": "v2", "model": "flux-kit-en"}}, "greeting": "Thanks for calling!"}}'
                  value={settings || ""}
                  onChange={onSettingsChange}
                  className={className}
                  disabled={disabled}
                  rows={8}
                  ariaLabel="Full Deepgram Settings JSON"
                  ariaDescribedBy={settingsHelpId}
                  ariaRequired={isRequired("deepgram_realtime_settings")}
                  error={settingsError}
                />
                <p
                  id={settingsHelpId}
                  className="text-xs text-coval-text-muted"
                >
                  Deep-merged over Coval defaults. Use this for provider
                  endpoints, fallbacks, turn-taking, keyterms, audio sample
                  rates, third-party voices, and any newer Deepgram field. The
                  system prompt and tool definitions above take precedence over
                  duplicate values here.
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
